import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AccessOverviewNode,
  AccessOverviewPerson,
} from 'screens/AuditScreens/components/AccessOverview';
import styles from 'screens/AuditScreens/screens/AuditScreen/AuditScreen.module.scss';
import { useQuery } from '@apollo/react-hooks';
import { AuditScreenQuery } from 'services/apollo/auditQueries/auditQueries';
import { AuthLoader } from 'screens/LoginScreen';
import { AccessFetchError } from 'screens/AuditScreens/components/AccessFetchError/AccessFetchError';
import { useTranslation } from 'react-i18next';
import shieldSRC from 'assets/img/shield.svg';
import { AsyncStorage } from 'services';
import { Storage } from 'services';

export const AuditScreen = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState<undefined | string>();
  const [criticalIssues, setCriticalIssues] = useState(0);
  const [warningIssues, setWarningIssues] = useState(0);

  const { error, data } = useQuery(AuditScreenQuery, {
    variables: { first: 20, cursor: '' },
    fetchPolicy: 'cache-only',
    errorPolicy: 'all',
  });

  function updateOpenSection(section: string) {
    AsyncStorage.set('openAuditSection', section);
    setOpenSection(section);
  }

  /**
   * Update critical and warning issues
   */
  React.useEffect(() => {
    if (data !== undefined) {
      var totalCriticalIssues = 0;
      let allSections = Object.entries(data.documentSecurityAudit) as any;
      allSections.splice(-1, 1); // remove openLinks
      let potentiallyCriticalSections = allSections.filter(function (obj: any) {
        return obj[0] !== 'openLinksAccess';
      });
      for (var i = 0; i < potentiallyCriticalSections.length; i++) {
        if (potentiallyCriticalSections[i][1].pageInfo.total > 0) {
          totalCriticalIssues++;
        }
      }
      setCriticalIssues(totalCriticalIssues);
      setWarningIssues(data.documentSecurityAudit.openLinksAccess.pageInfo.total > 0 ? 1 : 0);
    }
  }, [data]);

  if (data === undefined) {
    return <AuthLoader type="DSA" />;
  }

  if (error) {
    return <AccessFetchError />;
  }

  /**
   * Generate subtitle message in header
   * based on critical and warning issues
   */
  function subtitleMessage() {
    const base = t('auditScreen.audit-screen-subtitle-base');

    if (!warningIssues && !criticalIssues) {
      return (
        <span>
          {base}
          {t('auditScreen.audit-screen-subtitle-no-issues')}
        </span>
      );
    } else if (warningIssues && !criticalIssues) {
      return (
        <>
          {base}
          <strong>{warningIssues}</strong>
          {t('auditScreen.audit-screen-subtitle-warning')}
        </>
      );
    } else if (!warningIssues && criticalIssues) {
      return (
        <>
          {base}
          <strong>{criticalIssues}</strong>
          {t('auditScreen.audit-screen-subtitle-critical')}
        </>
      );
    } else {
      return (
        <>
          {base}
          <strong>{criticalIssues}</strong>
          {t('auditScreen.audit-screen-subtitle-critical')}
          {' and '}
          <strong>{warningIssues}</strong>
          {t('auditScreen.audit-screen-subtitle-warning')}
        </>
      );
    }
  }

  function renderSection(section: string, openByDefault?: boolean) {
    /**
     * When rendering for the first time, if openSection is undefined
     * and the openByDefault fn param is true, we open the section automatically
     */
    let openSectionFromStorage = Storage.get('openAuditSection') as any;

    if (openSectionFromStorage && openSection !== openSectionFromStorage) {
      setOpenSection(openSectionFromStorage);
    }

    if (openByDefault && openSection === undefined) {
      setOpenSection(section);
      return null;
    }

    if (section === 'outsideOrganizationAccess') {
      return (
        <div className={styles.section}>
          <AccessOverviewPerson
            people={data.documentSecurityAudit.outsideOrganizationAccess.contacts}
            total={data.documentSecurityAudit.outsideOrganizationAccess.pageInfo.total}
            onViewAll={() => history.push('/dsa/outside-organization')}
            title={t('auditScreen.outside-organization-summary-title')}
            subtitleOpen={`${data.documentSecurityAudit.outsideOrganizationAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.outside-organization-summary-subtitle-open'
            )}`}
            subtitleClosed={`${data.documentSecurityAudit.outsideOrganizationAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.outside-organization-summary-subtitle-closed'
            )}`}
            buttonMessage={t('auditScreen.outside-organization-summary-subtitle-button')}
            isOpen={openSection === 'outsideOrganizationAccess' ? true : false}
            handleTriggerClick={() => {
              if (openSection !== 'outsideOrganizationAccess') {
                updateOpenSection('outsideOrganizationAccess');
              } else if (data.documentSecurityAudit.outsideOrganizationAccess.pageInfo.total > 0) {
                updateOpenSection('');
              }
            }}
          />
        </div>
      );
    }

    if (section === 'personalAccountAccess') {
      return (
        <div className={styles.section}>
          <AccessOverviewPerson
            people={data.documentSecurityAudit.personalAccountAccess.contacts}
            total={data.documentSecurityAudit.personalAccountAccess.pageInfo.total}
            onViewAll={() => history.push('/dsa/personal-accounts')}
            title={t('auditScreen.personal-account-summary-title')}
            subtitleOpen={`${data.documentSecurityAudit.personalAccountAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.personal-account-summary-subtitle-open'
            )}`}
            subtitleClosed={`${data.documentSecurityAudit.personalAccountAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.personal-account-summary-subtitle-closed'
            )}`}
            buttonMessage={t('auditScreen.personal-account-summary-subtitle-button')}
            isOpen={openSection === 'personalAccountAccess' ? true : false}
            handleTriggerClick={() => {
              if (openSection !== 'personalAccountAccess') {
                updateOpenSection('personalAccountAccess');
              } else if (data.documentSecurityAudit.personalAccountAccess.pageInfo.total > 0) {
                updateOpenSection('');
              }
            }}
          />
        </div>
      );
    }

    if (section === 'outsideOwnershipAccess') {
      return (
        <div className={styles.section}>
          <AccessOverviewPerson
            people={data.documentSecurityAudit.outsideOwnershipAccess.contacts}
            total={data.documentSecurityAudit.outsideOwnershipAccess.pageInfo.total}
            onViewAll={() => history.push('/dsa/outside-ownership')}
            title={t('auditScreen.outside-ownership-summary-title')}
            subtitleOpen={`${data.documentSecurityAudit.outsideOwnershipAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.outside-ownership-summary-subtitle-open'
            )}`}
            subtitleClosed={`${data.documentSecurityAudit.outsideOwnershipAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.outside-ownership-summary-subtitle-closed'
            )}`}
            buttonMessage={t('auditScreen.outside-ownership-summary-subtitle-button')}
            isOpen={openSection === 'outsideOwnershipAccess' ? true : false}
            isOutsideOwner
            handleTriggerClick={() => {
              if (openSection !== 'outsideOwnershipAccess') {
                updateOpenSection('outsideOwnershipAccess');
              } else {
                updateOpenSection('');
              }
            }}
          />
        </div>
      );
    }

    if (section === 'openLinksAccess') {
      return (
        <div className={styles.section}>
          <AccessOverviewNode
            nodes={data.documentSecurityAudit.openLinksAccess.nodes}
            total={data.documentSecurityAudit.openLinksAccess.pageInfo.total}
            onViewAll={() => history.push('/dsa/open-links')}
            title={t('auditScreen.open-links-summary-title')}
            subtitleOpen={`${data.documentSecurityAudit.openLinksAccess.pageInfo.total.toLocaleString()} ${t(
              'auditScreen.open-links-summary-subtitle-open'
            )}`}
            subtitleClosed={`${data.documentSecurityAudit.openLinksAccess.pageInfo.total.toLocaleString()}  ${t(
              'auditScreen.open-links-summary-subtitle-closed'
            )}`}
            buttonMessage={t('auditScreen.open-links-summary-subtitle-button')}
            isOpen={openSection === 'openLinksAccess' ? true : false}
            handleTriggerClick={() => {
              if (openSection !== 'openLinksAccess') {
                updateOpenSection('openLinksAccess');
              } else if (data.documentSecurityAudit.openLinksAccess.pageInfo.total > 0) {
                updateOpenSection('');
              }
            }}
          />
        </div>
      );
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.iconContainer}>
          <img className={styles.icon} alt={'Shield Icon'} src={shieldSRC} />
          {warningIssues + criticalIssues > 0 && (
            <div className={styles.issueCount}>{warningIssues + criticalIssues}</div>
          )}
        </div>
        <div className={styles.dataContainer}>
          <div className={styles.title}>{t('auditScreen.audit-screen-main-title')}</div>
          <div className={styles.subtitle}>{subtitleMessage()}</div>
        </div>
      </div>

      {renderSection('outsideOrganizationAccess', true)}
      {renderSection('personalAccountAccess')}
      {renderSection('outsideOwnershipAccess')}
      {renderSection('openLinksAccess')}
    </div>
  );
};
